import { getDeviceAndUserStore } from '../storage/stores';
import {
  fetchDeviceProfileData,
  createDevice,
  replaceDevice,
  DeviceType,
} from '../api/device';
import { fetchProfileName, getProfileID, createProfile } from '../api/profile';
import { OnBoardingError } from '../lib/errors/OnBoardingError';
import { onConsumedQuotaUpdated } from '../screenTime';
import { identifySentry } from '../lib/vendor/sentry';

export const initDeviceAndUser = async (deviceUid: string) => {
  // Get and persist device info to the storage
  const deviceStore = getDeviceAndUserStore();
  const { profileId } = await fetchDeviceProfileData(deviceUid);
  const profileName = await fetchProfileName(profileId);
  await deviceStore.set({
    device: deviceUid,
    user: '1',
    consumedQuotaSeconds: 0,
    profileId,
    profileName,
  });

  // Identify Sentry with the deviceUID as soon as it's persisted to the storage
  identifySentry();
};

export const updateConsumedQuota = async (consumedQuotaSeconds: number) => {
  const deviceUserStore = getDeviceAndUserStore();
  const deviceUserStoreData = deviceUserStore.get();
  if (!deviceUserStoreData) {
    throw new Error('Device User Store not properly initialized');
  }

  const { device, user, profileId, profileName } = deviceUserStoreData;

  await deviceUserStore.set({
    device,
    user,
    consumedQuotaSeconds,
    profileId,
    profileName,
  });

  console.info(`Consumed quota updated to ${consumedQuotaSeconds} seconds.`);

  return onConsumedQuotaUpdated();
};

/**
 * Check if the profile exist, if not creates it,
 * check if the device exist, if not creates it,
 * and returns the deviceUID
 */
export const getOrCreateDeviceAndProfile = async (
  deviceName: string,
  profileName: string,
  deviceType: DeviceType
): Promise<string> => {
  const profileId = await getOrCreateUserProfileId(profileName);

  return getOrCreateUserDeviceUId(deviceName, profileId, deviceType);
};

export const getOrCreateUserProfileId = (
  profileName: string
): Promise<number> =>
  createOrApplyFn(
    () => createProfile(profileName).then(profile => profile.id),
    () => getProfileID(profileName) as Promise<number>
  );

export const getOrCreateUserDeviceUId = async (
  deviceName: string,
  profileId: number,
  deviceType: DeviceType
): Promise<string> =>
  createOrApplyFn(
    () =>
      createDevice(deviceName, profileId, deviceType).then(
        device => device.uid
      ),
    () => replaceDevice(deviceName, profileId, deviceType)
  );

/**
 * Creates a resource if it doesn't exist, otherwise applies a function.
 * @param createFn - Function that creates the resource. It should throw an
 * OnBoardingError if the resource already exists with a 409 status code.
 * @param fn - Fall back function that is applied if the resource already
 * exists. It should return the same type as the createFn.
 */
const createOrApplyFn = async <T>(
  createFn: () => Promise<T>,
  fn: () => Promise<T>
): Promise<T> => {
  const alreadyExistsErrorCode = 409;

  try {
    return await createFn();
  } catch (error) {
    if (
      error instanceof OnBoardingError &&
      error.statusCode === alreadyExistsErrorCode
    ) {
      return await fn();
    } else {
      throw error;
    }
  }
};
